import ctl from '@netlify/classnames-template-literals'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { CampaignStatusBadge } from '../../../constructs'
import { ConfirmationDialog, ConfirmationDialogRef, Glyphs } from '../../../rapid-ui'
import { CampaignModel } from '../../shared'
import { CampaignCommonContext } from './campaign-common-context'
import { ProvidedCampaignNavConfigContext } from './campaign-nav-context'

export type PortalCampaignNavProps = {
	activeCampaignNavLink?:
		| 'overview'
		| 'candidates'
		| 'targeting'
		| 'analytics'
		| 'invoice'
		| 'export'
		| 'access'
		| 'settings'
	campaign: CampaignModel
	isLoading: boolean
	onPullToDraft?: () => Promise<void>
}

export function PortalCampaignNav({
	activeCampaignNavLink,
	campaign,
	isLoading,
	onPullToDraft,
}: PortalCampaignNavProps) {
	const { t } = useTranslation('common')
	const { permissions } = React.useContext(CampaignCommonContext)
	const {
		overviewLinker,
		candidatesLinker,
		targetingLinker,
		analyticsLinker,
		invoiceLinker,
		exportLinker,
		accessLinker,
		settingsLinker,
	} = React.useContext(ProvidedCampaignNavConfigContext)

	const confirmationRef = React.useRef<ConfirmationDialogRef>(null)
	const [isPullingToDraft, setIsPullingToDraft] = React.useState(false)

	const handlePullToDraft = React.useCallback(async () => {
		if (!onPullToDraft) { return }

		try {
			await confirmationRef.current?.confirm({
				title: t('overview.nav.moveToDraftTitle'),
				message: t('overview.nav.moveToDraftMessage'),
				confirmText: t('overview.nav.moveToDraft'),
				cancelText: t('overview.nav.cancel'),
			})
			setIsPullingToDraft(true)
			try {
				await onPullToDraft()
				// No need to reset state on success - we're redirecting anyway
			} catch {
				setIsPullingToDraft(false)
				// Optionally re-throw or show error to user
			}
		} catch {
			// User cancelled the confirmation
		}
	}, [onPullToDraft])

	const campaignNavItemClassName = (active: boolean) =>
		ctl(`
		flex items-center gap-2 px-4 py-3
		text-base
		relative
		${active ? 'text-brand-600 font-medium' : 'text-grey-700 font-normal'}
		`)

	const campaignNavItemHighlightClassName = ctl(`
		absolute inset-x-[16px] bottom-0 h-[1px] bg-brand-600 rounded-full
	`)

	return (
		<div className="grid grid-cols-[1fr_auto] mx-auto justify-between max-w-[var(--max-width)] min-h-[var(--campaign-nav-height)]">
			<div data-is="campaign-nav-left" className="h-full grid grid-cols-[auto_1fr] gap-x-6">
				<div className="h-full flex items-center">
					<div
						className="size-16 rounded bg-grey-100"
						style={{
							backgroundImage: `url(${campaign.image})`,
							backgroundSize: 'cover',
							backgroundPosition: 'center',
						}}
					>
					</div>
				</div>
				<div className="h-full flex flex-col">
					{isLoading
						? (
							<div className="flex-1 flex items-end">
								<div className="text-2xl w-48 bg-grey-100 rounded animate-pulse">&nbsp;</div>
							</div>
						)
						: (
							<div className="flex-1 flex items-end text-2xl font-semibold">
								{campaign.title}
							</div>
						)}
					<nav className="flex -mx-4">
						{overviewLinker({
							campaignId: campaign.id,
							children: (
								<span className={campaignNavItemClassName(activeCampaignNavLink === 'overview')}>
									{t('overview.nav.overview')}
									{activeCampaignNavLink === 'overview' && (
										<span className={campaignNavItemHighlightClassName} />
									)}
								</span>
							),
						})}
						{candidatesLinker({
							campaignId: campaign.id,
							children: (
								<span className={campaignNavItemClassName(activeCampaignNavLink === 'candidates')}>
									{t('overview.nav.candidates')}
									{activeCampaignNavLink === 'candidates' && (
										<span className={campaignNavItemHighlightClassName} />
									)}
								</span>
							),
						})}
						{permissions.canConfigureTargeting && targetingLinker({
							campaignId: campaign.id,
							children: (
								<span className={campaignNavItemClassName(activeCampaignNavLink === 'targeting')}>
									{t('overview.nav.targeting')}
									{activeCampaignNavLink === 'targeting' && (
										<span className={campaignNavItemHighlightClassName} />
									)}
								</span>
							),
						})}
						{permissions.canSeeAnalytics && analyticsLinker({
							campaignId: campaign.id,
							children: (
								<span className={campaignNavItemClassName(activeCampaignNavLink === 'analytics')}>
									{t('overview.nav.analytics')}
									{activeCampaignNavLink === 'analytics' && (
										<span className={campaignNavItemHighlightClassName} />
									)}
								</span>
							),
						})}
						{permissions.canSeeInvoice && invoiceLinker({
							campaignId: campaign.id,
							children: (
								<span className={campaignNavItemClassName(activeCampaignNavLink === 'invoice')}>
									{t('overview.nav.invoice')}
									{activeCampaignNavLink === 'invoice' && (
										<span className={campaignNavItemHighlightClassName} />
									)}
								</span>
							),
						})}
						{accessLinker({
							campaignId: campaign.id,
							children: (
								<span className={campaignNavItemClassName(activeCampaignNavLink === 'access')}>
									{t('overview.nav.access')}
									{activeCampaignNavLink === 'access' && (
										<span className={campaignNavItemHighlightClassName} />
									)}
								</span>
							),
						})}
						{permissions.canSeeExport && exportLinker({
							campaignId: campaign.id,
							children: (
								<span className={campaignNavItemClassName(activeCampaignNavLink === 'export')}>
									Export
									{activeCampaignNavLink === 'export' && (
										<span className={campaignNavItemHighlightClassName} />
									)}
								</span>
							),
						})}
						{settingsLinker({
							campaignId: campaign.id,
							children: (
								<span className={campaignNavItemClassName(activeCampaignNavLink === 'settings')}>
									{t('overview.nav.settings')}
									{activeCampaignNavLink === 'settings' && (
										<span className={campaignNavItemHighlightClassName} />
									)}
								</span>
							),
						})}
					</nav>
				</div>
			</div>
			<div data-is="campaign-nav-right" className="self-start  mt-4">
				{!isLoading && (
					<div className="flex items-center gap-6">
						{campaign.status === 'review' && onPullToDraft && (
							<button
								onClick={handlePullToDraft}
								disabled={isPullingToDraft}
								className={ctl(`
									text-sm text-purple-800 px-3 py-1.5 rounded
									ring ring-purple-200 hover:bg-purple-50 transition-colors
									${isPullingToDraft ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
								`)}
							>
								{isPullingToDraft ? t('overview.nav.moving') : t('overview.nav.moveToDraft')}
							</button>
						)}
						<CampaignStatusBadge status={campaign.status} />

						<div className="flex items-center gap-1 text-sm text-grey-600">
							<Glyphs.micro.PinIcon />
							{campaign.location}
						</div>
					</div>
				)}
			</div>
			<ConfirmationDialog ref={confirmationRef} />
		</div>
	)
}
